import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Target } from "lucide-react";
import { fmtCurrency, fmtPct, fmtRatio, fmtDays, fmtNumber } from "../lib/format";

const POSITION_COLORS = {
  good: "#2C5C43",
  ok: "#5e7a4d",
  watch: "#9c6b1d",
  bad: "#A84632",
  na: "#5C665D",
};

const POSITION_LABEL = {
  good: "Top of range",
  ok: "Healthy",
  watch: "Below median",
  bad: "Out of range",
  na: "—",
};

function formatValue(v, fmt, currency = "USD") {
  if (v === null || v === undefined) return "—";
  switch (fmt) {
    case "pct": return fmtPct(v, 1);
    case "ratio": return fmtRatio(v);
    case "days": return fmtDays(v);
    case "money": return fmtCurrency(v, currency);
    case "number":
      if (v >= 999) return "12+ mo";
      return fmtNumber(v, 0);
    default: return String(v);
  }
}

function formatRange(range, fmt, currency = "USD") {
  if (!range) return "";
  const [low, , high] = range;
  if (fmt === "money" && (low === 0 && high === 0)) return "size yardstick";
  return `Typical ${formatValue(low, fmt, currency)} – ${formatValue(high, fmt, currency)}`;
}

export default function IndustrySpotlight({ kpis, industryLabel, currency = "USD" }) {
  if (!kpis || kpis.length === 0) return null;
  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none" data-testid="industry-spotlight-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Target size={18} strokeWidth={1.5} className="text-[#C26D5C]" />
          <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
            Industry spotlight
          </CardDescription>
        </div>
        <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
          {industryLabel} — the metrics that actually matter.
        </CardTitle>
        <p className="text-sm text-[#5C665D] mt-1 leading-relaxed">
          Beyond the universal ratios, here are the operating KPIs that bankers, accountants and
          smart operators in this sector watch first.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {kpis.map((kpi) => {
            const color = POSITION_COLORS[kpi.position] || POSITION_COLORS.na;
            const positionLabel = POSITION_LABEL[kpi.position] || "—";
            return (
              <div
                key={kpi.key}
                className="border-b border-[#F0EDE5] pb-5 last:border-b-0 last:pb-0"
                data-testid={`industry-kpi-${kpi.key}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="text-sm font-medium text-[#1A1A1A]">{kpi.label}</h4>
                    {kpi.what_it_means && (
                      <p className="text-xs text-[#5C665D] leading-relaxed mt-1 max-w-2xl">
                        {kpi.what_it_means}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-serif-display text-2xl text-[#1F382E] tabular-nums leading-none">
                      {formatValue(kpi.value, kpi.fmt, currency)}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.18em] mt-1.5" style={{ color }}>
                      <span className="inline-block h-1.5 w-1.5 rounded-full mr-1 align-middle" style={{ background: color }} />
                      {positionLabel}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-[#5C665D]">
                  <span>{formatRange(kpi.range, kpi.fmt, currency)}</span>
                </div>
                {kpi.improve_levers && kpi.improve_levers.length > 0 && kpi.position !== "good" && kpi.position !== "ok" && (
                  <ul className="mt-3 space-y-1">
                    {kpi.improve_levers.map((lever, i) => (
                      <li key={i} className="text-xs text-[#1A1A1A] flex gap-2">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-[#C26D5C] shrink-0" />
                        <span>{lever}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
